// Reservation return form: shown when the guest comes back via a signed URL
// (?r=TOKEN) after Julie sends a quote. The new token is an opaque HMAC of the
// booking_id (no payload) — the form cannot pre-fill from it. Pre-fill data
// comes from the Firestore booking doc loaded by a Cloud Function
// (loadBookingByToken). Submits to reservations/{id} with the opaque token for
// server-side verification; the intake_reservation CF resolves the booking_id
// and reads the lead from Firestore to merge the full record.
//
// UX contract (session 34, Stefanie's "it will not let me"): CONFIRMED data is
// rendered as a static summary — never as readOnly inputs. readOnly fields
// look editable but refuse input, so a non-technical guest reads the page as
// broken. The only fields on the page are the ones the guest can actually
// fill (address, ID, consent). And a load failure gets an explicit error +
// retry state — never an empty form full of locked fields.
import { useState, useEffect } from "preact/hooks";
import { PAYMENT_CHANNEL_LABELS } from "../config.js";
import { validateReservation } from "../lib/validation.js";
import { submitReservation } from "../lib/firestore.js";
import { describeError, reportError, SUPPORT_PHONE } from "../lib/errlog.js";
import { uploadIdDoc } from "../lib/storage.js";
import { loadBookingByToken } from "../lib/api.js";
import { fmtDate } from "../lib/dates.js";
import { useCanCapture } from "../lib/useCanCapture.js";
import { TextField } from "./Fields.jsx";
import { SmsConsent } from "./SmsConsent.jsx";
import { Status } from "./Status.jsx";

export function ReservationReturn({ token, onDone }) {
  const [form, setForm] = useState({
    lead_ref: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    checkin_date: "",
    checkout_date: "",
    adults: "1",
    children: "0",
    wedding: "no",
    address: "",
    payment_channel: "",
    channel: "",
    sms_consent: false,
  });
  const [idFile, setIdFile] = useState(null);
  const [idMethod, setIdMethod] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [invalid, setInvalid] = useState(false);
  const [expired, setExpired] = useState(null);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [emailNeededAtLoad, setEmailNeededAtLoad] = useState(false);
  const canCapture = useCanCapture();

  useEffect(() => {
    if (!token?.raw) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setInvalid(false);
    loadBookingByToken(token.raw)
      .then((b) => {
        if (cancelled) return;
        if (!b) {
          // 401/404 — EITHER the link itself is bad, OR a middlebox answered
          // for the server (corporate firewall/DNS filter blocking
          // cloudfunctions.net — Stefanie's office desktop, session 34). The
          // copy below covers both; report so we can see the pattern.
          reportError("reservation_invalid_link", new Error("token rejected (401/404)"));
          setInvalid(true);
          setLoading(false);
          return;
        }
        const stage = b.stage;
        const channel = b.channel || "";
        const intakeDone = String(b.vrbo_intake_complete || "").toLowerCase() === "yes";
        if (stage === "confirmed" || stage === "prepared" || stage === "sesamed" || stage === "wrapped" || stage === "closed" || stage === "lost") {
          if (channel === "vrbo" && !intakeDone) {
            // Confirmed VRBO guest who hasn't filled intake (address + ID).
            // Show the form so they can complete it.
          } else {
            setExpired("confirmed");
            setLoading(false);
            return;
          }
        }
        if (stage === "lead") {
          setExpired("pending");
          setLoading(false);
          return;
        }
        setForm((f) => ({
          ...f,
          lead_ref: b.booking_id || b.lead_ref || "",
          first_name: b.first_name || "",
          last_name: b.last_name || "",
          email: b.email || "",
          phone: b.phone_e164 || b.phone || "",
          checkin_date: b.checkin_date || "",
          checkout_date: b.checkout_date || "",
          adults: String(b.adults || "1"),
          children: String(b.children || "0"),
          wedding: b.wedding || "no",
          address: "",
          payment_channel: b.payment_channel || "",
          channel: b.channel || "",
        }));
        // VRBO withholds the guest's email (relay addresses are blanked
        // server-side) — when nothing usable is on file, the email becomes a
        // FILLABLE field. Captured once at load: the field must not vanish
        // while the guest is typing into it.
        setEmailNeededAtLoad(!b.email);
        setAgreementSigned(String(b.agreement_signed || "").toLowerCase() === "yes");
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Transient failure (offline, CF hiccup): retry-able error state —
        // NEVER an empty form full of locked fields.
        reportError("reservation_load", err);
        setLoadError(describeError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token.raw, loadAttempt]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    // Clear the field's error as soon as the guest fixes it — a stale red
    // error under a now-valid field reads as "still broken".
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  }

  function pickId(file, method) {
    setIdFile(file || null);
    setIdMethod(file ? method : null);
    setErrors((e) => (e.idFile ? { ...e, idFile: undefined } : e));
  }

  async function submit(e) {
    e.preventDefault();
    const isVrbo = form.channel === "vrbo";
    const { errors: e2, phone } = validateReservation({ ...form, idFile }, isVrbo);
    setErrors(e2);
    if (Object.keys(e2).length > 0) return;
    setBusy(true);
    setStatus(null);
    try {
      const id_doc_path = await uploadIdDoc(idFile);
      const payload = { ...form, phone_e164: phone, id_doc_path, _token: token.raw };
      if (isVrbo) delete payload.payment_channel;
      await submitReservation(payload);
      onDone?.({ firstName: form.first_name, flow: agreementSigned ? "vrbo_intake" : "reservation" });
    } catch (err) {
      reportError("reservation_submit", err);
      setStatus({ kind: "err", text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div class="surface">
        <h2>Complete your reservation</h2>
        <p class="band">Loading your reservation…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div class="surface">
        <h2>Complete your reservation</h2>
        <Status kind="err">{loadError}</Status>
        <p class="band">Nothing was lost — your link is still good.</p>
        <button type="button" class="cal-retry" onClick={() => setLoadAttempt((a) => a + 1)}>
          Try again
        </button>
      </div>
    );
  }

  if (invalid) {
    return (
      <div class="surface">
        <h2>We couldn't load your booking</h2>
        <p>If you're on an office, hotel or other shared network, its firewall may be
        blocking the booking server — <strong>try switching to mobile data</strong> and tap
        Try again. Otherwise the link may be incomplete: ask Julie for a fresh one,
        call or text <strong>{SUPPORT_PHONE}</strong>.</p>
        <button type="button" class="cal-retry" onClick={() => setLoadAttempt((a) => a + 1)}>
          Try again
        </button>
      </div>
    );
  }

  if (expired === "confirmed") {
    return (
      <div class="surface">
        <h2>Your reservation is already confirmed!</h2>
        <p>If you need to make changes, please contact Julie at {SUPPORT_PHONE}.</p>
      </div>
    );
  }

  if (expired === "pending") {
    return (
      <div class="surface">
        <h2>Your quote is being prepared</h2>
        <p>Julie will send it to you shortly.</p>
      </div>
    );
  }

  const isVrboChannel = form.channel === "vrbo";
  const partyText = `${form.adults} adult${form.adults === "1" ? "" : "s"}` +
    (form.children && form.children !== "0" ? `, ${form.children} children` : "");
  // VRBO withholds the guest's email (and relay addresses are never used) —
  // when the booking loaded with no usable email on file, the agreement has
  // nowhere to go. The email then becomes a FILLABLE field (required), not a
  // locked summary value: the only fields on the page are the ones the guest
  // can actually fill.
  const needsEmail = emailNeededAtLoad;

  // Submit-time clarity: after a failed submit the guest must see EXACTLY
  // what's missing without hunting for red text — one banner right above the
  // button (it re-renders on every attempt, and per-field errors still point
  // at the spot). Guest-fixable items get plain words; anything else (a
  // prefilled value that failed validation) routes to Julie, since the guest
  // can't edit it here.
  const MISSING_LABELS = {
    email: "your email address",
    address: "your full address",
    idFile: "a photo of your ID",
    sms_consent: "consent to booking texts",
  };
  const missingKeys = Object.keys(errors).filter((k) => errors[k]);
  const guestMissing = missingKeys.filter((k) => MISSING_LABELS[k]).map((k) => MISSING_LABELS[k]);
  const dataMissing = missingKeys.filter((k) => !MISSING_LABELS[k]);

  return (
    <form class="surface" onSubmit={submit} noValidate>
      <h2>Complete your reservation</h2>
      <p class="band">Welcome back! Just a few more details to lock in your stay.</p>

      {/* Confirmed data is TEXT, not locked inputs — a readOnly field looks
          editable but refuses input, which reads as "the page is broken". */}
      <div class="booking-summary">
        <p class="summary-name">{form.first_name} {form.last_name}</p>
        <p class="summary-line">{[form.email, form.phone].filter(Boolean).join(" · ")}</p>
        <p class="summary-line">{fmtDate(form.checkin_date)} – {fmtDate(form.checkout_date)} · {partyText}</p>
        {isVrboChannel ? <p class="summary-line">Booked via VRBO</p> : null}
        {!isVrboChannel && form.wedding === "yes" ? <p class="summary-line">Wedding: yes</p> : null}
        {!isVrboChannel && form.payment_channel ? (
          <p class="summary-line">Payment method: {PAYMENT_CHANNEL_LABELS[form.payment_channel] || form.payment_channel}</p>
        ) : null}
      </div>

      <p class="band">Your details above are already on file. We just need your <strong>address</strong> and a <strong>photo of your ID</strong>:</p>

      {needsEmail ? (
        <>
          <TextField
            label="Email for your rental agreement"
            field="email"
            type="email"
            value={form.email}
            onInput={set}
            error={errors.email}
            placeholder="you@example.com"
            required
          />
          <p class="hint">VRBO doesn't share your email with us - we need it to send your rental agreement and arrival details.</p>
        </>
      ) : null}

      <TextField label="Full address" field="address" value={form.address} onInput={set} error={errors.address} placeholder="Street, City, State, ZIP" required />
      <label class="field">
        <span class="field-label">Photo of your ID<span class="req">*</span></span>
        <div class="id-choices">
          {canCapture ? (
            <label class={"id-choice id-choice-primary" + (idMethod === "camera" ? " id-choice-selected" : "")}>
              {idMethod === "camera" ? "✓ Photo captured" : "\u{1F4F7} Take Photo"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => pickId(e.currentTarget.files?.[0], "camera")}
                aria-invalid={!!errors.idFile}
              />
            </label>
          ) : null}
          <label class={(canCapture ? "id-choice id-choice-secondary" : "id-choice id-choice-primary") + (idMethod === "upload" ? " id-choice-selected" : "")}>
            {idMethod === "upload"
              ? "✓ " + (idFile && idFile.name && idFile.name.length > 30 ? idFile.name.slice(0, 27) + "..." : (idFile && idFile.name) || "File selected")
              : (canCapture ? "\u{1F4CE} Upload File" : "\u{1F4CE} Upload ID Photo")}
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => pickId(e.currentTarget.files?.[0], "upload")}
              aria-invalid={!!errors.idFile}
            />
          </label>
        </div>
        {errors.idFile ? <span class="field-error">{errors.idFile}</span> : null}
        <span class="hint">{idFile
          ? "Got it. " + (idFile.size ? Math.round(idFile.size / 1024) + " KB. " : "") + "Used only to verify your reservation. Deleted at checkout plus 14 days."
          : "Used only to verify your reservation. Deleted at checkout plus 14 days."}</span>
      </label>
      <SmsConsent
        value={form.sms_consent}
        onChange={(v) => set("sms_consent", v)}
        error={errors.sms_consent}
      />
      {missingKeys.length > 0 ? (
        <div class="missing-banner" role="alert">
          {guestMissing.length > 0 ? (
            <p><strong>Still missing:</strong> {guestMissing.join(" · ")}</p>
          ) : null}
          {dataMissing.length > 0 ? (
            <p>Some booking details need Julie's attention — text her at {SUPPORT_PHONE} and she'll fix them with you.</p>
          ) : null}
        </div>
      ) : null}
      <button type="submit" disabled={busy}>{busy ? "Submitting..." : "Submit reservation"}</button>
      <Status kind={status?.kind === "err" ? "err" : "ok"}>{status?.text}</Status>
    </form>
  );
}
